import { profilePath } from "@travily/routes";
import type ImageType from "@travily/types/shared/Image";
import { Card, CardImage, CardTitle } from "@ui/components/Card";
import { Icon } from "@ui/components/Icon";
import type { CardTheme } from "@ui/types/card";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";

// Members are families and individuals; both serialize avatar/name/handle/initial
// identically, so one card renders either. Mirrors the homepage family card
// (profile picture + name), plus the member's current location — the one fact
// that makes this a roster of people on the move rather than a product grid.

export interface MemberCardMember {
  handle: string;
  display_name: string;
  initial: string;
  type: "Family" | "Individual";
  avatar: ImageType | null;
  /** Current location name (present only while the member is on a trip). */
  location_label?: string | null;
}

export interface MemberCardProps {
  className?: string;
  member: MemberCardMember;
  /** Visual treatment: "editorial" for spacious layouts, "compact" for dense grids. */
  theme?: CardTheme;
}

export function MemberCard({ className, member, theme = "editorial" }: MemberCardProps) {
  return (
    <Card className={className ?? "gap-2 lg:gap-2.5"} href={profilePath(member.handle)} linkLabel={member.display_name}>
      <CardImage
        alt={member.display_name}
        fallback={
          <AccountAvatar
            account={{ profile_type: member.type, initial: member.initial }}
            className="size-full rounded-none text-6xl"
          />
        }
        image={member.avatar}
        overlayInset="sm"
      />
      <div className="flex flex-col gap-0.5">
        <CardTitle theme={theme}>{member.display_name}</CardTitle>
        {member.location_label ? (
          <p className="line-clamp-1 flex items-center gap-1 text-sm text-neutral-500">
            <Icon name="location" size="16" />
            {member.location_label}
          </p>
        ) : null}
      </div>
    </Card>
  );
}
